import {AxiosRequestConfig} from 'axios';
import { DatasetsSearchResult } from 'types/types';
import {APIPromise, APIResponse, requestPromiseTypeWrapper} from "../services/HttpService";
import {ApiDefinition} from "./ApiDefinition";
import {BaseDatasetDTO} from "./model/dataset/BaseDatasetDTO";
import {DatasetInfo} from "./model/dataset/DatasetInfo";
import {DatasetCategoryDTO} from "./model/datset/DatasetCategoryDTO";
import { DatasetQueryParams } from './model/dataset/DatasetDataInfoResponse';


const promise = requestPromiseTypeWrapper;

class DatasetsApiDefinition extends ApiDefinition {

    constructor() {
        super("/datasets")
    }

    async getDatasetCategories(): APIPromise<DatasetCategoryDTO[]> {
        try {
            const {data} = await this.httpClient.get(`${this.apiScopeUrl}/categories`);
            const obj = data.map((it: Partial<DatasetCategoryDTO>) => new DatasetCategoryDTO(it))
            return {data: obj}
        } catch (error: any) {
            return {error: error.response?.data};
        }
    }

    async getBaseDataset(identifier: string | undefined | null): APIPromise<BaseDatasetDTO | null> {
        if (!identifier) {
            return {data: null};
        }

        try {
            const {data} = await this.httpClient.get(`${this.apiScopeUrl}/${identifier}`);
            const obj = new BaseDatasetDTO(data)
            return {data: obj}
        } catch (error: any) {
            return {error: error.response?.data};
        }
    }

    async queryDatasets(params: DatasetQueryParams): APIPromise<DatasetInfo[]> {
        const formattedParams = {
            ...params,
            query: params.query || (params.allowEmptyQuery ? "" : null),
            identifiers: params.identifiers?.join(",") || undefined,
        };

        return promise(this.httpClient.get(`${this.apiScopeUrl}/search`, { params: formattedParams }), data => {
            return data.map(item => new DatasetInfo(item!));
        });
    }

    async getDatasetInfo(identifier: string | null): APIPromise<DatasetInfo> {
        if(!identifier) return {data: undefined};
        return promise(this.httpClient.get(`${this.apiScopeUrl}/search`, {params: {identifiers: identifier}}), (data: any) => {
            return new DatasetInfo(data[0]);
        });
    }

    async getDatasetsInfo(identifiers: Array<string | null>): APIPromise<Array<DatasetInfo>> {
        const ids = identifiers.filter(e => e!=null)
        if(!ids) return {data: undefined};

        return promise(this.httpClient.get(`${this.apiScopeUrl}/search`, {params: {identifiers: ids.join(",")}}), (data: any) => {
            return data.map((item:any) => new DatasetInfo(item))
        });
    }

    async getDatasetsCount(): APIPromise<number> {
        return this.get("count");
    }

    async putUpdateDatasetInfo(datasetInfo: BaseDatasetDTO, fullUpdate?: boolean): APIPromise<BaseDatasetDTO> {
        const config: AxiosRequestConfig = {
            params: {
                fullUpdate
            }
        };
    
        return this.put(`${datasetInfo.identifier}`, datasetInfo, config);
    }

    async getDefaultDatasetCategory():APIPromise<DatasetCategoryDTO> {
        return this.get("categories/default")
    }

    async searchDatasetsUsage(identifiers: string[]): APIPromise<DatasetsSearchResult[]> {
      return this.post("search-usage", identifiers);
    }

    async createNewCategorizedDataset(data: Partial<BaseDatasetDTO>): APIPromise<BaseDatasetDTO> {
        return this.post("create-categorized", data)
    }

    async deleteDataset(identifier: string): APIPromise<any> {
        return this.delete(`${identifier}/soft-delete`);
    }

    async hardDeleteDataset(identifier: string): APIPromise<any> {
        return this.delete(`${identifier}/hard-delete`);
    }

    async getDatasetsByTags(categoryId: number | undefined, tags: string): APIPromise<DatasetInfo[]> {
        const datasetsByTags: APIResponse<DatasetInfo[]> = await this.get(`byTags?categoryId=${categoryId}&tags=${tags}`);
        const filteredDatasetsByTags = datasetsByTags.data?.filter((item: DatasetInfo) => !item.deleted) ?? [];
        return {
            ...datasetsByTags,
            data: filteredDatasetsByTags
        };
    }  
}

const DatasetsApi = new DatasetsApiDefinition();

export default DatasetsApi;
